import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Search } from "@mui/icons-material";
import { CircularProgress, FormControl, FormHelperText, FormLabel, InputAdornment, Autocomplete as MuiAutocomplete, Popper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { createContext, forwardRef, useContext, useEffect, useId, useRef } from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { VariableSizeList } from "react-window";
const LISTBOX_PADDING = 8;
const renderRow = (props)=>{
    const { data, index, style } = props;
    const [optionProps, label] = data[index];
    const { key: _key, ...rest } = optionProps;
    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING
    };
    return /*#__PURE__*/ _jsx(Typography, {
        component: "li",
        ...rest,
        noWrap: true,
        style: inlineStyle,
        children: label
    });
};
const OuterElementContext = /*#__PURE__*/ createContext({});
const OuterElementType = /*#__PURE__*/ forwardRef((props, ref)=>{
    const outerProps = useContext(OuterElementContext);
    /* MUI's listbox props carry their own onScroll, which would otherwise replace react-window's and stop rows rendering */ const onScroll = (event)=>{
        props.onScroll?.(event);
        outerProps.onScroll?.(event);
    };
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        ...props,
        ...outerProps,
        onScroll: onScroll
    });
});
OuterElementType.displayName = 'OuterElementType';
const useResetCache = (itemCount)=>{
    const ref = useRef(null);
    useEffect(()=>{
        ref.current?.resetAfterIndex(0, true);
    }, [
        itemCount
    ]);
    return ref;
};
const ListboxComponent = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { children, ...other } = props;
    const items = children;
    const itemCount = items.length;
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true
    });
    const itemSize = isCompact ? 36 : 48;
    const height = itemCount > 8 ? 8 * itemSize : itemCount * itemSize;
    const gridRef = useResetCache(itemCount);
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        children: /*#__PURE__*/ _jsx(OuterElementContext.Provider, {
            value: other,
            children: /*#__PURE__*/ _jsx(VariableSizeList, {
                height: height + 2 * LISTBOX_PADDING,
                innerElementType: "ul",
                itemCount: itemCount,
                itemData: items,
                itemSize: ()=>itemSize,
                outerElementType: OuterElementType,
                overscanCount: 5,
                ref: gridRef,
                width: "100%",
                children: renderRow
            })
        })
    });
});
ListboxComponent.displayName = 'ListboxComponent';
const StyledPopper = styled(Popper)(({ theme })=>({
        [`& .MuiAutocomplete-listbox`]: {
            '& li': {
                '&.Mui-focused, &:hover': {
                    backgroundColor: theme.palette.action.hover
                },
                '&[aria-selected="true"]': {
                    backgroundColor: theme.palette.action.selected
                },
                '&[aria-selected="true"].Mui-focused, &[aria-selected="true"]:hover': {
                    backgroundColor: theme.palette.action.selected
                },
                boxSizing: 'border-box',
                cursor: 'pointer',
                padding: '6px 16px'
            },
            '& ul': {
                margin: 0,
                padding: 0
            },
            boxSizing: 'border-box',
            padding: 0
        },
        zIndex: theme.zIndex.modal
    }));
export const Autocomplete = (props)=>{
    const labelId = useId();
    const errorId = useId();
    const formContext = useFormContext();
    const translation = useTranslation();
    const dataComponent = props['data-component'] ?? 'autocomplete';
    const dataTestid = props['data-testid'] ?? props.name;
    const getOptionLabel = props.getOptionLabel ?? ((option)=>option.label);
    const isOptionEqualToValue = props.isOptionEqualToValue ?? ((option, value)=>option.value === value.value);
    const getOptionValue = props.getOptionValue;
    const isMultiple = props.multiple === true;
    const error = get(formContext.formState.errors, props.name);
    /* MUI blanks the input whenever the `value` prop's identity changes, so a
     selection rebuilt on every render wipes what the user is typing */ const selectedRef = useRef(null);
    const isSameOption = (a, b)=>getOptionLabel(a) === getOptionLabel(b) && (getOptionValue ? getOptionValue(a) === getOptionValue(b) : a === b);
    const resolveValue = (fieldValue)=>{
        const previous = selectedRef.current;
        if (isMultiple) {
            const values = Array.isArray(fieldValue) ? fieldValue : [];
            const next = getOptionValue ? values.map((value)=>props.options.find((option)=>getOptionValue(option) === value)).filter((option)=>option !== undefined) : values;
            const isUnchanged = Array.isArray(previous) && previous.length === next.length && next.every((option, index)=>isSameOption(option, previous[index]));
            if (isUnchanged) return previous;
            selectedRef.current = next;
            return next;
        }
        const next = fieldValue === null || fieldValue === undefined ? null : getOptionValue ? props.options.find((option)=>getOptionValue(option) === fieldValue) ?? null : fieldValue;
        if (next && previous && !Array.isArray(previous)) {
            if (isSameOption(next, previous)) return previous;
        }
        selectedRef.current = next;
        return next;
    };
    const handleChange = (fieldOnChange, value)=>{
        if (isMultiple) {
            const arr = value ?? [];
            const next = getOptionValue ? arr.map(getOptionValue) : arr;
            fieldOnChange(next);
            props.onChange?.(arr);
            return;
        }
        const single = value;
        const next = getOptionValue ? single === null ? null : getOptionValue(single) : single;
        fieldOnChange(next);
        props.onChange?.(single);
    };
    return /*#__PURE__*/ _jsx(Controller, {
        control: formContext.control,
        name: props.name,
        render: ({ field })=>{
            const selected = resolveValue(field.value);
            const onInputChange = (_e, value, reason)=>{
                if (reason === 'clear') {
                    handleChange(field.onChange, isMultiple ? [] : null);
                }
                /**
           * Only typing and clearing may drive a remote lookup. MUI also fires
           * this with reason 'reset' when an option is picked (it rewrites the
           * input to the selection, or blanks it when multiple), and refetching
           * there swaps the option list out from under the open listbox: the
           * virtualized rows are reused, so a click already in flight lands on
           * whichever option now occupies that row. That silently filed the
           * grid under a different record than the one the user picked.
           */ if (reason !== 'input' && reason !== 'clear') {
                    return;
                }
                props.onInputChange?.(reason === 'clear' ? '' : value);
            };
            const autocomplete = /*#__PURE__*/ _jsx(MuiAutocomplete, {
                autoComplete: true,
                autoHighlight: true,
                "data-component": dataComponent,
                ...props.disableClearable ? {
                    disableClearable: true
                } : {},
                disableListWrap: true,
                ...props.onInputChange ? {
                    filterOptions: (options)=>options
                } : {},
                getOptionLabel: getOptionLabel,
                isOptionEqualToValue: isOptionEqualToValue,
                ...isMultiple ? {
                    multiple: true
                } : {},
                noOptionsText: props.noOptionsText,
                onBlur: field.onBlur,
                onChange: (_e, value)=>handleChange(field.onChange, value),
                onInputChange: onInputChange,
                openOnFocus: true,
                options: props.options,
                renderInput: (params)=>/*#__PURE__*/ _jsx(TextField, {
                        ...params,
                        error: !!error,
                        placeholder: props.placeholder,
                        size: "small",
                        slotProps: {
                            htmlInput: {
                                ...params.inputProps,
                                ...props.label && {
                                    'aria-labelledby': labelId
                                },
                                'data-testid': dataTestid
                            },
                            input: {
                                ...params.InputProps,
                                endAdornment: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        props.loading && /*#__PURE__*/ _jsx(CircularProgress, {
                                            "data-testid": dataTestid,
                                            size: "1rem"
                                        }),
                                        params.InputProps.endAdornment
                                    ]
                                }),
                                startAdornment: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(InputAdornment, {
                                            position: "start",
                                            children: /*#__PURE__*/ _jsx(Search, {
                                                fontSize: "small"
                                            })
                                        }),
                                        isMultiple && params.InputProps.startAdornment
                                    ]
                                })
                            }
                        }
                    }),
                renderOption: (optionProps, option, state)=>[
                        optionProps,
                        getOptionLabel(option),
                        state.index
                    ],
                size: "small",
                slots: {
                    listbox: ListboxComponent,
                    popper: StyledPopper
                },
                sx: [
                    {
                        backgroundColor: '#ffffff',
                        ...props.fullWidth ? {
                            width: '100%'
                        } : {
                            width: 320
                        }
                    },
                    ...Array.isArray(props.sx) ? props.sx : [
                        props.sx
                    ]
                ],
                value: selected
            });
            const helperText = !props.hideError ? /*#__PURE__*/ _jsx(FormHelperText, {
                "aria-atomic": "true",
                "data-component": "autocompleteError",
                "data-testid": dataTestid,
                id: errorId,
                children: translation.t(error?.message) || props.helperText || ' '
            }) : null;
            return /*#__PURE__*/ _jsxs(FormControl, {
                error: !!error,
                fullWidth: props.fullWidth,
                required: props.required,
                children: [
                    props.label && /*#__PURE__*/ _jsx(FormLabel, {
                        id: labelId,
                        required: props.required,
                        children: props.label
                    }),
                    autocomplete,
                    helperText
                ]
            });
        }
    });
};
Autocomplete.displayName = 'Autocomplete';

//# sourceMappingURL=Autocomplete.js.map